import React, { useEffect, useRef } from 'react';
import './Profile.css';

const Profile: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // 各セクションのmain-textへの参照
  const whoIAmTextRef = useRef<HTMLDivElement>(null);
  const whatIOfferTextRef = useRef<HTMLDivElement>(null);
  const getInTouchTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // プロフィールページ表示時にbody/htmlのスクロールを無効化
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';

    // 既存のLenisを完全に破棄（念のため）
    if ((window as any).profileLenisCleanup) {
      (window as any).profileLenisCleanup();
      (window as any).profileLenisCleanup = null;
    }

    if (!scrollRef.current || !contentRef.current) return;

    // プロフィールページ専用のLenis初期化
    const initProfileLenis = () => {
      import('@studio-freight/lenis').then((module) => {
        const Lenis = module.default;

        const lenis = new (Lenis as any)({
          wrapper: scrollRef.current,
          content: contentRef.current,
          duration: 1.2,
          smooth: true,
          smoothTouch: true, // スマホでもLenisを有効化
          direction: 'vertical',
          infinite: false, // 無限スクロールを無効化
          touchMultiplier: 2, // タッチ感度を調整
        });

        console.log('✅ Profile Lenis initialized for profile-content-section');

        // アニメーションループ
        const raf = (time: number) => {
          lenis.raf(time);
          requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        // クリーンアップをwindowオブジェクトに保存
        (window as any).profileLenisCleanup = () => {
          lenis.destroy();
          console.log('✅ Profile Lenis destroyed');
        };
      }).catch((error) => {
        console.error('❌ Profile Lenis initialization failed:', error);
      });
    };

    initProfileLenis();

    return () => {
      // Lenisクリーンアップ
      if ((window as any).profileLenisCleanup) {
        (window as any).profileLenisCleanup();
        (window as any).profileLenisCleanup = null;
      }

      // body/htmlのスクロールを復元
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  // 動的フォントサイズ調整ロジック（br固定行対応版）
  useEffect(() => {
    const updateFontSize = () => {
      const contentSection = document.querySelector('.profile-content-section');
      if (!contentSection) {
        console.log('❌ profile-content-section not found');
        return;
      }
      
      const containerWidth = contentSection.clientWidth;
      console.log('📏 Profile content section width:', containerWidth);
      
      if (containerWidth === 0) {
        console.log('⏳ Container width is 0, retrying in 100ms...');
        setTimeout(updateFontSize, 100);
        return;
      }
      
      // 各main-textを更新
      const textRefs = [whoIAmTextRef, whatIOfferTextRef, getInTouchTextRef];
      
      textRefs.forEach((textRef, index) => {
        if (textRef.current) {
          const textElement = textRef.current;
          
          // br固定行の場合：コンテナ幅のみでフォントサイズを決定
          // 文字数による調整は削除
          let baseFontSize = Math.min(45, Math.max(20, containerWidth * 0.05));
          
          // !importantに対抗するため、style属性に!importantを追加
          textElement.style.setProperty('font-size', `${baseFontSize}px`, 'important');
          
          console.log(`📏 Section ${index + 1} font size: ${baseFontSize}px (container width: ${containerWidth}px)`);
        }
      });
    };

    // 複数のタイミングで実行して確実にサイズを取得
    updateFontSize();
    setTimeout(updateFontSize, 100);
    setTimeout(updateFontSize, 500);
    
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, []);

  // Instagram遷移ハンドラー
  const handleInstagramClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://instagram.com/osada.sun', '_blank', 'noopener,noreferrer');
  };

  // X(Twitter)遷移ハンドラー
  const handleXClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://x.com/osadasun', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* 左側：固定画像 */}
        <div className="profile-image-section">
          <img 
            src="/images/Kosuke.png" 
            alt="OSADA Profile" 
            className="profile-image"
          />
        </div>
        
        {/* 右側：スクロール可能なテキスト（Lenis適用） */}
        <div className="profile-content-section" ref={scrollRef}>
          <div className="profile-content" ref={contentRef}>
            
            {/* Who I am セクション */}
            <div className="profile-section">
              <h3 className="section-title">Who I am</h3>
              <div className="main-text" ref={whoIAmTextRef}>
                INDIE DEVELOPER, PERSONAL Photo & Videographer.<br/>
                I GREW UP IN YAMANASHI, <br/>
                GRADUATED FROM KEIO UNIVERSITY'S SYSTEM
                DESIGN ENGINEERING DEPARTMENT.
              </div>
            </div>

            {/* Service セクション */}
            <div className="profile-section">
              <h3 className="section-title">What I offer</h3>
              <div className="main-text" ref={whatIOfferTextRef}>
                MOBILE DESIGN & DEV<br/>
                WEB DESIGN & DEV<br/>
                PHOTO & VIDEO<br/>
                (AND GOOD JOKES)
              </div>
            </div>

            {/* SNS セクション */}
            <div className="profile-section">
              <h3 className="section-title">SNS</h3>
              <div className="social-icons">
                <button
                  onClick={handleInstagramClick}
                  className="social-icon-button"
                  aria-label="Instagram"
                >
                  <img src="/images/Instagram.png" alt="Instagram" />
                </button>
                <button
                  onClick={handleXClick}
                  className="social-icon-button"
                  aria-label="X (Twitter)"
                >
                  <img src="/images/Twitter.png" alt="X (Twitter)" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;